import { formatPrice } from "@/lib/utils";
import { useAppSelector } from "@/redux/features/hooks";
import {
  MenuFormulaOrderItem,
  MenuFormulaOrderItemSelection,
} from "@/redux/services/order/order.type";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useRestaurantCurrency } from "@/lib/hooks";

export const FormulaOrderItem: React.FC<{
  item: MenuFormulaOrderItem;
  currency?: string;
}> = ({ item, currency: currencyProp }) => {
  const currentRestaurant = useAppSelector(
    (state) => state.user.currentRestaurant
  );
  const hookCurrency = useRestaurantCurrency();
  const currency = currencyProp ?? hookCurrency;

  const renderSelection = (selection: MenuFormulaOrderItemSelection) => {
    const name =
      selection.menuFormulaComponentItem?.name ??
      selection.menuItem?.title ??
      selection.menuFormulaComponentItem?.menuItem?.title ??
      "";

    const groupedOptions =
      selection.selectedOptions?.reduce(
        (acc: Record<string, any[]>, opt: any) => {
          const key = opt.menuItemOption?.title ?? "Option";
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(opt);
          return acc;
        },
        {}
      ) ?? {};

    return (
      <ul className="list-disc pl-5 text-xs">
        <li>
          <span className="text-grayMedium font-medium">
            {name}
          </span>
          {selection.menuFormulaComponentItem?.priceModifier > 0 && (
            <span className="text-accent">
              {" "}
              (+
              {formatPrice(
                selection.menuFormulaComponentItem.priceModifier / 100,
                currency
              )}
              )
            </span>
          )}
          {Object.keys(groupedOptions).length > 0 && (
            <div className="ml-1 mt-0.5">
              {Object.entries(groupedOptions).map(
                ([optionTitle, values]) => (
                  <p
                    key={optionTitle}
                    className="text-xs text-muted-foreground"
                  >
                    {optionTitle}:{" "}
                    {values
                      .map((v: any) => v.title)
                      .join(", ")}
                  </p>
                )
              )}
            </div>
          )}
        </li>
      </ul>
    );
  };

  return (
    <div
      className={`flex items-start gap-3 py-2 px-2 rounded-lg ${
        item.isModified ? "bg-yellow-50" : ""
      }`}
    >
      <img
        src={
          item?.menuFormula?.image?.url ??
          currentRestaurant?.image?.url ??
          "/images/icon.png"
        }
        alt={item.menuFormula.name}
        className="rounded-lg h-10 w-10 object-cover flex-shrink-0"
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-sm font-medium text-card-foreground">
              {item.menuFormula.name}
            </p>
            <p className="text-xs mt-0.5">
              {formatPrice(item.menuFormula.price / 100, currency)}
            </p>
            {item.selections.map(
              (selection: MenuFormulaOrderItemSelection) => (
                <div key={selection.id}>{renderSelection(selection)}</div>
              )
            )}
            {item.additionalDemands && (
              <p className="text-xs text-muted-foreground mt-0.5">
                Note: {item.additionalDemands}
              </p>
            )}
          </div>

          <div className="flex flex-col items-end flex-shrink-0">
            {item.isModified && (
              <span className="bg-yellow-200 text-yellow-800 px-1.5 py-0.5 rounded text-[10px] font-medium mb-1">
                Modifié
                {item.lastModifiedAt &&
                  ` ${format(new Date(item.lastModifiedAt), "HH'h'mm", { locale: fr })}`}
              </span>
            )}
            <span className="text-sm text-muted-foreground">
              x{item.quantity}
            </span>
            <span className="text-sm font-semibold text-card-foreground">
              {formatPrice(item.price / 100, currency)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
